import type { ComponentType } from "react";
import { AlertTriangle, CheckCircle2, Monitor, Smartphone, XCircle } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { CalloutPin, type TabCalloutProps } from "./CalloutPin";
import {
  CWV_CALLOUTS,
  CWV_DESKTOP,
  CWV_MOBILE,
  STATUS_GOOD,
  STATUS_NEEDS,
  STATUS_POOR,
  SURFACE,
  type Callout,
  type VitalsRow,
} from "./gscData";
import {
  formatNumber,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_ITEM_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "./chartTheme";

interface IconProps {
  className?: string;
  style?: React.CSSProperties;
}

/** Status is never color alone — icon + text label + count, always. */
function LegendItem({
  icon: Icon,
  color,
  label,
  count,
}: {
  icon: ComponentType<IconProps>;
  color: string;
  label: string;
  count: string;
}) {
  return (
    <span className="flex items-center gap-2.5 text-xl">
      <Icon className="h-6 w-6 shrink-0" style={{ color }} />
      <span className="text-white/80">{label}</span>
      <span className="font-semibold tabular-nums text-white/95">{count}</span>
    </span>
  );
}

function kShort(v: number): string {
  if (v < 1000) return `${v}`;
  const k = Math.round((v / 1000) * 10) / 10;
  return `${k}K`;
}

interface DeviceCardProps extends TabCalloutProps {
  data: VitalsRow;
  icon: ComponentType<IconProps>;
  /** Optional pin at the card's top-right (① Mobile). */
  cardPin?: Callout;
  /** Optional pin on the legend row (③ the three vitals). */
  legendPin?: Callout;
}

function DeviceCard({ data, icon: Icon, cardPin, legendPin, ...pinProps }: DeviceCardProps) {
  const total = data.good + data.needsImprovement + data.poor;
  return (
    <div className="relative flex flex-col justify-center gap-6 rounded-2xl border border-white/10 bg-white/[0.03] p-6">
      <div className="flex items-center gap-3">
        <Icon className="h-7 w-7 text-white/60" />
        <span className="font-display text-3xl font-semibold text-white/95">{data.device}</span>
        <span className="ml-auto pr-10 text-xl text-white/45">{kShort(total)} URLs</span>
      </div>

      {/* One horizontal stacked bar — 2px surface gaps, rounded outer ends only */}
      <div className="h-[56px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={[data]}
            layout="vertical"
            margin={{ top: 8, right: 4, bottom: 8, left: 4 }}
          >
            <XAxis type="number" hide domain={[0, total]} />
            <YAxis type="category" dataKey="device" hide />
            <Tooltip
              contentStyle={TOOLTIP_CONTENT_STYLE}
              labelStyle={TOOLTIP_LABEL_STYLE}
              itemStyle={TOOLTIP_ITEM_STYLE}
              cursor={{ fill: "rgba(255,255,255,0.04)" }}
              formatter={(value) => formatNumber(value)}
            />
            <Bar
              dataKey="good"
              name="Good"
              stackId="cwv"
              fill={STATUS_GOOD}
              stroke={SURFACE}
              strokeWidth={2}
              barSize={24}
              radius={[4, 0, 0, 4]}
            />
            <Bar
              dataKey="needsImprovement"
              name="Needs improvement"
              stackId="cwv"
              fill={STATUS_NEEDS}
              stroke={SURFACE}
              strokeWidth={2}
              barSize={24}
            />
            <Bar
              dataKey="poor"
              name="Poor"
              stackId="cwv"
              fill={STATUS_POOR}
              stroke={SURFACE}
              strokeWidth={2}
              barSize={24}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="relative flex flex-wrap items-center gap-x-8 gap-y-2 pr-12">
        <LegendItem
          icon={CheckCircle2}
          color={STATUS_GOOD}
          label="Good"
          count={kShort(data.good)}
        />
        <LegendItem
          icon={AlertTriangle}
          color={STATUS_NEEDS}
          label="Needs improvement"
          count={kShort(data.needsImprovement)}
        />
        <LegendItem icon={XCircle} color={STATUS_POOR} label="Poor" count={kShort(data.poor)} />
        {legendPin && (
          <CalloutPin
            callout={legendPin}
            className="absolute right-0 top-1/2 -translate-y-1/2"
            {...pinProps}
          />
        )}
      </div>

      {cardPin && (
        <CalloutPin callout={cardPin} className="absolute right-4 top-4" {...pinProps} />
      )}
    </div>
  );
}

export function VitalsTab({ openId, visited, onOpen }: TabCalloutProps) {
  const pinProps = { openId, visited, onOpen };
  return (
    <div className="flex h-full flex-col gap-4">
      <div className="grid min-h-0 flex-1 grid-cols-2 gap-4">
        <DeviceCard
          data={CWV_MOBILE}
          icon={Smartphone}
          cardPin={CWV_CALLOUTS.mobile}
          legendPin={CWV_CALLOUTS.legend}
          {...pinProps}
        />
        <DeviceCard data={CWV_DESKTOP} icon={Monitor} {...pinProps} />
      </div>

      {/* Failing group — pages fail in template groups */}
      <div
        className="relative flex shrink-0 items-center gap-5 rounded-2xl border border-white/10 px-6 py-4"
        style={{
          borderLeft: `4px solid ${STATUS_NEEDS}`,
          background: "rgba(251,191,36,0.05)",
        }}
      >
        <AlertTriangle className="h-8 w-8 shrink-0 text-accent-amber" />
        <div>
          <p className="text-2xl font-medium text-white/95">Article template</p>
          <p className="text-xl text-white/70">INP over 200ms on mobile</p>
        </div>
        <p className="ml-auto pr-8 font-sans text-4xl font-semibold text-accent-amber">
          1,700 <span className="text-xl font-normal text-white/60">URLs</span>
        </p>
        {/* ② Fix one template, fix every URL it renders */}
        <CalloutPin
          callout={CWV_CALLOUTS.template}
          className="absolute -right-3 -top-3"
          {...pinProps}
        />
      </div>
    </div>
  );
}
